import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { MODULES, setSelectedModule } from "../appModule/appModuleSlice";
import { logout } from "../authentication/authSlice";

// Resolve module-scoped flags from the active app module so every create/approve
// call carries isLab (Makers Lab) / isClassroom (Eduspace) without each caller
// having to pass them.
const getModuleFlags = (thunkAPI) => {
  const selectedModule = thunkAPI.getState()?.appModule?.selectedModule;
  return {
    isLab: selectedModule === MODULES.MAKERSLAB,
    isClassroom: selectedModule === MODULES.EDUSPACE,
    isBmb: selectedModule === MODULES.BLOOMBERG,
  };
};

// Key stamped onto a rooms response with the module that was active when the
// request went out, so a late response from a module the user has already left
// can be discarded (see the getAvailableClassrooms fulfilled reducer).
const REQUEST_MODULE_KEY = "__requestModule";

let userId;
let token;
let headers;
let authData = Cookies.get("loginData")
? JSON.parse(Cookies.get("loginData"))
: null;
let API = process.env.REACT_APP_API;
let rolesData = Cookies.get("roles")
? JSON.parse(Cookies.get("roles"))
: null;
const currentUrl = window.location.hostname;
const isMakersLabTheme = currentUrl.includes('makerslab.mastersunion.org') || currentUrl.includes('127.0.0.1') 
// || currentUrl.includes('localhost') ;
let organizationId = isMakersLabTheme ? '52c3b93c-59ba-44e7-8e99-bd5fc889b68f' : authData?.organizationId;

const setTokenValues = () => {
  
  const data = Cookies.get("loginData")
    ? JSON.parse(Cookies.get("loginData"))
    : null;
  userId = data?.id;
  
  token = Cookies.get("token");
  headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

// Create classroom booking request
export const createClassroomRequest = createAsyncThunk(
  "classroom/createRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      
      // Get organizationId dynamically from cookies
      const authData = Cookies.get("loginData")
        ? JSON.parse(Cookies.get("loginData"))
        : null;
      const rolesData = Cookies.get("roles")
        ? JSON.parse(Cookies.get("roles"))
        : null;
      const dynamicOrganizationId = isMakersLabTheme
        ? '52c3b93c-59ba-44e7-8e99-bd5fc889b68f'
        : null;

      const { isLab, isClassroom, isBmb } = getModuleFlags(thunkAPI);

      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/create-request`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            request_id: obj?.request_id ?? null,
            room_id: obj.room_id,
            requestedBy: userId,
            date: obj.date,
            slot: obj.slot,
            meeting_title: obj.meeting_title,
            school : obj.school,
            school_id : obj.school_id || null,
            organizationId : dynamicOrganizationId,
            attendees : obj.attendees,
            equipments : obj.equipments,
            needInstructor : obj.needInstructor,
            isEvent : obj.isEvent ?? false,
            participants : obj.participants ?? 1,
            isLab,
            isClassroom,
            isBmb
          }),
        }
      );
      let data = await response.json();
      if (response.status === 201) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get my classroom booking requests
export const getMyClassroomRequests = createAsyncThunk(
  "classroom/getMyRequests",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/my-booking-requests/${userId}?page=${obj.pageNumber}&count=${obj.perPageCount}&eventStatus=${obj.eventStatus ?? ''}&searchQuery=${encodeURIComponent(obj.searchQuery ?? '')}&isLab=${obj.isLab ?? ''}&isBmb=${obj.isBmb ?? ''}&date=${obj.date ?? ''}`,
        {
          method: "GET",
          headers,
          // Aborting the dispatched thunk (useLatestRequest) has to abort the
          // HTTP request as well — otherwise the superseded duplicate still
          // runs to completion on the server.
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      // Let RTK turn a deliberate abort into a `meta.aborted` rejection.
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get all pending classroom booking requests (for admin)
export const getAllPendingClassroomRequests = createAsyncThunk(
  "classroom/getAllPendingRequests",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      
      // Get organizationId dynamically from cookies
      const authData = Cookies.get("loginData")
        ? JSON.parse(Cookies.get("loginData"))
        : null;
      const rolesData = Cookies.get("roles")
        ? JSON.parse(Cookies.get("roles"))
        : null;
      const dynamicOrganizationId = isMakersLabTheme
        ? '52c3b93c-59ba-44e7-8e99-bd5fc889b68f' 
        : null;
      
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/pending-requests/${obj.pageNumber}/${obj.perPageCount}?organizationId=${dynamicOrganizationId ?? ''}&searchQuery=${obj.searchQuery ?? ''}&tower=${obj.filters.tower ?? ''}&room=${obj.filters.room ?? ''}&status=${obj.filters.status ?? ''}&createdBy=${obj.filters.createdBy ?? ''}&isLab=${obj.isLab ?? ''}&isBmb=${obj.isBmb ?? ''}&startTime=${encodeURIComponent(obj.filters.startTime ?? '')}&endTime=${encodeURIComponent(obj.filters.endTime ?? '')}&date=${obj.filters.date ?? ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get approved classroom booking requests (for timeline selector)
export const getApprovedClassroomRequests = createAsyncThunk(
  "classroom/getApprovedRequests",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/pending-requests?isLab=${obj.isLab}&isBmb=${obj.isBmb ?? ''}&status=Approved,Under Review`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Approve classroom booking request
export const approveClassroomRequest = createAsyncThunk(
  "classroom/approveRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const { isLab, isClassroom, isBmb } = getModuleFlags(thunkAPI);
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/approve/${obj.requestId}?isLab=${isLab}&isClassroom=${isClassroom}&isBmb=${isBmb}`,
        {
          method: "PUT",
          headers,
        }
      );
      let data = await response.json();
      // Backend returns 200 with { success: false } for business-logic
      // failures (e.g. conflicting slots), so check both.
      if (response.status === 200 && data?.success !== false) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Bulk Status Change classroom booking request
export const bulkStatusChangeClassroomRequest = createAsyncThunk(
  "classroom/bulkStatusChangeRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/bulk-statusChange`,
        {
          method: "PUT",
          headers,
          body : JSON.stringify({
            requestIds : obj.requestIds,
            action : obj.action,
            reason_for_rejection : obj.reason_for_rejection,
            adminId : obj.adminId
          })
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Reject classroom booking request
export const rejectClassroomRequest = createAsyncThunk(
  "classroom/rejectRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/reject/${obj.requestId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            reason_for_rejection: obj.reason_for_rejection,
            status : obj.status,
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Revoke classroom booking request
export const revokeClassroomRequest = createAsyncThunk(
  "classroom/revokeRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/revoke/${obj.requestId}`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({
            reason_for_rejection: 'Request revoked by the administrator',
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Update booking + request time (admin schedule edit on the dashboard calendar)
export const updateClassroomBookingTime = createAsyncThunk(
  "classroom/updateBookingTime",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/update-booking-time`,
        {
          method: "PUT",
          headers,
          body: JSON.stringify({ updates: obj.updates }),
        }
      );
      let data = await response.json();
      if (response.status === 200 && data?.success !== false) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Edit the DETAILS of a booking request (title / school / participants / event
// flag / slot) from the dashboard calendar. Only a request that is still "Under
// Review" may be edited — once it is approved or rejected the details are
// frozen, so the caller gates on the status and the backend re-checks it.
export const updateClassroomRequest = createAsyncThunk(
  "classroom/updateRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const { isLab, isClassroom, isBmb } = getModuleFlags(thunkAPI);
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/update-request/${obj.requestId}`,
        {
          method: "PUT",
          headers,
          // Same field names as create-request, so the backend can share its
          // validation/normalisation for the request body.
          body: JSON.stringify({
            room_id: obj.room_id,
            date: obj.date,
            slot: obj.slot,
            meeting_title: obj.meeting_title,
            school: obj.school,
            // Omitted (undefined → dropped by JSON) leaves the stored school alone.
            school_id: obj.school_id === undefined ? undefined : obj.school_id || null,
            attendees: obj.attendees,
            equipments: obj.equipments ?? [],
            needInstructor: obj.needInstructor ?? false,
            isEvent: obj.isEvent ?? false,
            isLab,
            isClassroom,
            isBmb,
          }),
        }
      );
      let data = await response.json();
      if (response.status === 200 && data?.success !== false) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Duplicate a booking into another room/time (calendar copy-paste / Alt-drag)
export const duplicateClassroomBooking = createAsyncThunk(
  "classroom/duplicateBooking",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/duplicate-booking`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            sourceBookingId: obj.sourceBookingId || null,
            // A copy of a still-pending request has no booking row to duplicate
            // from, so it is copied from the request instead.
            sourceRequestId: obj.sourceRequestId || null,
            room_id: obj.room_id,
            start_time: obj.start_time,
            end_time: obj.end_time,
          }),
        }
      );
      let data = await response.json();
      // 201 on success; 200 with { success:false } for a destination conflict.
      if (response.status === 201 && data?.success !== false) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get available classroom rooms (reusing cowork API)
export const getAvailableClassrooms = createAsyncThunk(
  "classroom/getAvailableRooms",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      
      // Get organizationId dynamically from cookies
      const authData = Cookies.get("loginData")
        ? JSON.parse(Cookies.get("loginData"))
        : null;
      const rolesData = Cookies.get("roles")
        ? JSON.parse(Cookies.get("roles"))
        : null;
      const dynamicOrganizationId = isMakersLabTheme
        ? '52c3b93c-59ba-44e7-8e99-bd5fc889b68f'
        : null;

      // Snapshot the module this request belongs to, before awaiting.
      const requestModule = thunkAPI.getState()?.appModule?.selectedModule;

      const response = await fetch(
        `${API}/api/cowork/rooms/fetchAvailableAllRoomsandSlots/${userId}/${obj.pageNumber}/${obj.perPageCount}?organizationId=${dynamicOrganizationId}&meetingDate=${obj.meetingDate}&endDate=${obj.endDate || obj.meetingDate}&searchQuery=${obj.searchValue}&tower=${obj.filters.tower}&capacity=${obj.filters.capacity}&isStudent=${obj.isStudent}&isAdmin=${obj.isAdmin}&isClassAdmin=${obj.isClassAdmin}&isLab=${obj.isLab}&isBmb=${obj.isBmb ?? ''}&category=${obj.filters.category}&equipment=${obj.filters.equipment}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );

      let data = await response.json();
      if (response.status === 200) {
        return { ...data, [REQUEST_MODULE_KEY]: requestModule };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

export const getEduspaceCalendar = createAsyncThunk(
  "classroom/getEduspaceCalendar",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();

      // Snapshot the module this request belongs to, before awaiting.
      const requestModule = thunkAPI.getState()?.appModule?.selectedModule;

      const response = await fetch(
        `${API}/api/cowork/rooms/fetchEduspaceBookingRequests/${obj.pageNumber}/${obj.perPageCount}?meetingDate=${obj.meetingDate}&endDate=${obj.endDate || obj.meetingDate}&searchQuery=${obj.searchValue}&tower=${obj.filters.tower}&capacity=${obj.filters.capacity}&isClassAdmin=${obj.isClassAdmin}&isLab=${obj.isLab}&isBmb=${obj.isBmb ?? ''}`,
        {
          method: "GET",
          headers,
          signal: thunkAPI.signal,
        }
      );

      let data = await response.json();
      if (response.status === 200) {
        return { ...data, [REQUEST_MODULE_KEY]: requestModule };
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      // Let RTK turn a deliberate abort into a `meta.aborted` rejection.
      if (error.name === "AbortError") throw error;
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get Single classroom
export const getSingleRoom = createAsyncThunk(
  "classroom/getSingleRoomBookingRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/getSingleRoomBookingRequest/${obj.requestId}`,
        {
          method: "GET",
          headers,
        }
      );
      
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);


// Find slots for one classroom
export const findSlotsForOneClassroom = createAsyncThunk(
  "classroom/findSlotsForOneRoom",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/rooms/fetchOneRoomSlots/${userId}/${obj.roomId}?meetingDate=${obj.meetingDate}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// A user cancels their own booking request. Cancels the request AND its confirmed
// booking, so the slot (and, on Bloomberg, its seats) is genuinely released.
export const cancelClassroomRequest = createAsyncThunk(
  "classroom/cancelRequest",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/cancel-request/${obj.requestId}`,
        {
          method: "PUT",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Get all comments on a booking request (Makers Lab / Eduspace requests only)
export const getRequestComments = createAsyncThunk(
  "classroom/getRequestComments",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      // type: 'booking' (My Bookings / All Bookings) | 'dashboard' (Eduspace
      // calendar). Defaults to 'booking' so existing callers are unaffected.
      const type = obj.type || "booking";
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/request/${obj.requestId}/comments?type=${type}`,
        {
          method: "GET",
          headers,
        }
      );
      let data = await response.json();
      if (response.status === 200) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

// Add a comment to a booking request (Makers Lab / Eduspace requests only)
export const addRequestComment = createAsyncThunk(
  "classroom/addRequestComment",
  async (obj, thunkAPI) => {
    try {
      setTokenValues();
      const response = await fetch(
        `${API}/api/cowork/roomBookingAdmin/request/${obj.requestId}/comments`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            comment: obj.comment,
            comment_type: obj.type || "booking",
          }),
        }
      );
      let data = await response.json();
      if (response.status === 201) {
        return data;
      } else {
        return thunkAPI.rejectWithValue(data);
      }
    } catch (error) {
      console.log(error, "err");
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  }
);

const initialStateValues = {
  isClassroomSliceFetching: false,
  isClassroomSliceSmallFetching: false,
  // Per-list loading flags. `isClassroomSliceFetching` is shared by every thunk
  // in this slice, so a screen showing two lists at once (e.g. the Eduspace
  // dashboard's calendar + request table) can't tell which one is loading.
  // These narrow it down to a single request each.
  isPendingRequestsFetching: false,
  isAvailableClassroomsFetching: false,
  // My Bookings needs its own: the tab shares a screen with the room list, so
  // the generic flag was true whenever EITHER was loading and the tab couldn't
  // tell whether its own list was still on the way.
  isMyRequestsFetching: false,
  isClassroomSliceSuccess: false,
  isClassroomSliceError: false,
  classroomSliceSuccessMessage: "",
  classroomSliceErrorMessage: "",
  classroomSelectionDetail: null,
  availableClassroomsList: [],
  towersList : [],
  availableClassroomsCount: 0,
  myClassroomRequestsList: [],
  myClassroomRequestsCount: 0,
  allPendingRequestsList: [],
  allPendingRequestsCount: 0,
  approvedRequestsList: [],
  selectedDate: null,
  selectedSlot: null,
  selectedSchool : isMakersLabTheme ? 'Masters Union' : null,
  slotsForOneClassroom: [],
  availableClassroomMyBookings: [],
  selectedSingleClassRoom : null,
  isgettingSingleRoom : false,
  gettingSingleRoomSuccess : false,
  singleRoomErrorMessage : "",
  finalMultipleSlotsForAdmin: [],
  // Booking request comments (Makers Lab / Eduspace only)
  requestCommentsList: [],
  requestCommentsCount: 0,
  isCommentsFetching: false,
  isPostingComment: false,
  commentPostSuccess: false,
  // Module the currently-held room data belongs to. Set on every module switch so
  // a response from the previous module can be discarded instead of being merged
  // into the new module's list. null = not switched yet this session (first load).
  dataModule: null,
};

const classroomSlice = createSlice({
  name: "classroom",
  initialState: initialStateValues,
  reducers: {
    clearClassroomSliceStates: (state, action) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isPendingRequestsFetching = false;
      state.isAvailableClassroomsFetching = false;
      state.isMyRequestsFetching = false;
      state.isClassroomSliceSuccess = false;
      state.isClassroomSliceError = false;
      state.classroomSliceSuccessMessage = "";
      state.classroomSliceErrorMessage = "";
      return state;
    },
    clearClassroomSliceData: (state, action) => {
      state.availableClassroomsList = [];
      state.towersList = [];
      state.myClassroomRequestsList = [];
      state.allPendingRequestsList = [];
      state.approvedRequestsList = [];
      state.slotsForOneClassroom = [];
      state.availableClassroomsCount = 0;
      state.myClassroomRequestsCount = 0;
      state.allPendingRequestsCount = 0;
      return state;
    },
    clearClassroomDetail: (state, action) => {
      state.classroomSelectionDetail = null;
      return state;
    },
    addClassroomDetails: (state, action) => {
      state.classroomSelectionDetail = action.payload;
      return state;
    },
    addClassroomSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
      return state;
    },
    addClassroomSelectedSchool: (state,payload) => {
      state.selectedSchool = payload;
      return state;
    },
    addClassroomSelectedSlot: (state, action) => {
      state.selectedSlot = action.payload;
      return state;
    },
    clearClassroomSelectedSlot: (state, action) => {
      state.selectedSlot = null;
      return state;
    },
    addFinalMultipleSlotsForClassroomAdmin: (state, action) => {
      state.finalMultipleSlotsForAdmin = action.payload;
      return state;
    },
    clearFinalMultipleSlotsForClassroomAdmin: (state, action) => {
      state.finalMultipleSlotsForAdmin = [];
      return state;
    },
    clearSelectedSingleClassRoom: (state) => {
      state.selectedSingleClassRoom = null;
      return state;
    },
    clearRequestComments: (state) => {
      state.requestCommentsList = [];
      state.requestCommentsCount = 0;
      state.isCommentsFetching = false;
      state.isPostingComment = false;
      state.commentPostSuccess = false;
      return state;
    },
  },
  extraReducers: (builder) => {
    // Wipe everything on logout so the next user never sees the previous one's
    // classrooms/requests.
    builder.addCase(logout, () => initialStateValues);
    // Switching modules must not blend one module's classrooms/towers/requests
    // into another — drop the loaded lists so each module re-fetches its own.
    builder.addCase(setSelectedModule, (state, { payload }) => {
      state.dataModule = payload;
      state.availableClassroomsList = [];
      state.towersList = [];
      state.myClassroomRequestsList = [];
      state.allPendingRequestsList = [];
      state.approvedRequestsList = [];
      state.slotsForOneClassroom = [];
      state.availableClassroomMyBookings = [];
      state.classroomSelectionDetail = null;
      state.selectedSingleClassRoom = null;
      state.finalMultipleSlotsForAdmin = [];
      state.availableClassroomsCount = 0;
      state.myClassroomRequestsCount = 0;
      state.allPendingRequestsCount = 0;
    });
    // Create request
    builder.addCase(createClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = "Classroom booking request created successfully";
      return state;
    });
    builder.addCase(createClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(createClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceFetching = true;
      return state;
    });

    // Get my requests
    builder.addCase(getMyClassroomRequests.fulfilled, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isMyRequestsFetching = false;
      state.myClassroomRequestsList = [...state.myClassroomRequestsList, ...payload.data.requests];
      state.myClassroomRequestsCount = payload.data.totalCount;
      return state;
    });
    builder.addCase(getMyClassroomRequests.rejected, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isMyRequestsFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getMyClassroomRequests.pending, (state, { payload }) => {
      state.isClassroomSliceFetching = true;
      state.isClassroomSliceSmallFetching = true;
      state.isMyRequestsFetching = true;
      return state;
    });

    // Get all pending requests
    builder.addCase(getAllPendingClassroomRequests.fulfilled, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isPendingRequestsFetching = false;
      state.allPendingRequestsList = [...state.allPendingRequestsList, ...payload.data.requests];
      state.allPendingRequestsCount = payload.data.totalCount;
      return state;
    });
    builder.addCase(getAllPendingClassroomRequests.rejected, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isPendingRequestsFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAllPendingClassroomRequests.pending, (state, { payload }) => {
      state.isClassroomSliceFetching = true;
      state.isClassroomSliceSmallFetching = true;
      state.isPendingRequestsFetching = true;
      return state;
    });

    // Get approved requests
    builder.addCase(getApprovedClassroomRequests.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.approvedRequestsList = payload.data.requests;
      return state;
    });
    builder.addCase(getApprovedClassroomRequests.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getApprovedClassroomRequests.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Approve request
    builder.addCase(approveClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = "Request approved successfully";
      return state;
    });
    builder.addCase(approveClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(approveClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Reject request
    builder.addCase(rejectClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = "Request rejected successfully";
      return state;
    });
    builder.addCase(rejectClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(rejectClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Cancel own request (cancels the confirmed booking with it)
    builder.addCase(cancelClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = "Booking cancelled successfully";
      return state;
    });
    builder.addCase(cancelClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(cancelClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Revoke request
    builder.addCase(revokeClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = "Request revoked successfully";
      return state;
    });
    builder.addCase(revokeClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(revokeClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Bulk Status Change request
    builder.addCase(bulkStatusChangeClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = payload?.message || 'Bulk Status Changesd Successfully';
      return state;
    });
    builder.addCase(bulkStatusChangeClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(bulkStatusChangeClassroomRequest.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    builder.addCase(getSingleRoom.fulfilled, (state, { payload }) => {
      state.isgettingSingleRoom = false;
      state.gettingSingleRoomSuccess = true;
      state.selectedSingleClassRoom = payload;
      return state;
    });
    builder.addCase(getSingleRoom.rejected, (state, { payload }) => {
      state.isgettingSingleRoom = false;
      state.singleRoomErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    }); 
    builder.addCase(getSingleRoom.pending, (state, { payload }) => {
      state.isgettingSingleRoom = true;
      return state;
    });

    // Update booking time
    builder.addCase(updateClassroomBookingTime.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = payload?.message || "Booking time updated successfully";
      return state;
    });
    builder.addCase(updateClassroomBookingTime.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(updateClassroomBookingTime.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Edit an under-review request's details
    builder.addCase(updateClassroomRequest.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = payload?.message || "Booking request updated successfully";
      return state;
    });
    builder.addCase(updateClassroomRequest.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(updateClassroomRequest.pending, (state) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Duplicate booking
    builder.addCase(duplicateClassroomBooking.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceSuccess = true;
      state.classroomSliceSuccessMessage = payload?.message || "Booking duplicated successfully";
      return state;
    });
    builder.addCase(duplicateClassroomBooking.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(duplicateClassroomBooking.pending, (state) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // Get available classrooms
    builder.addCase(getAvailableClassrooms.fulfilled, (state, { payload, meta }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isAvailableClassroomsFetching = false;
      // Drop a response that was requested under a module the user has since
      // left. Without this an in-flight fetch (typically an infinite-scroll
      // "load more") resolves after the switch and appends the old module's
      // rooms into the new module's list — e.g. Eduspace rooms showing in CoWork.
      const requestModule = payload?.[REQUEST_MODULE_KEY];
      if (state.dataModule && requestModule && requestModule !== state.dataModule) {
        return state;
      }
      const incomingRooms = payload?.roomAndSlots || [];
      const incomingTowers = payload?.towers || [];
      // This list is shared by Makers Lab, Eduspace and Bloomberg (all fetch via
      // this thunk), so page 1 must REPLACE — otherwise a room loaded before a
      // logout / module switch lingers at the top even though it isn't in the new
      // response. Only "load more" (page > 1) appends.
      const isFirstPage = (meta?.arg?.pageNumber ?? 1) <= 1;
      state.availableClassroomsList = isFirstPage
        ? incomingRooms
        : [...state.availableClassroomsList, ...incomingRooms];
      state.towersList = isFirstPage
        ? incomingTowers
        : [...state.towersList, ...incomingTowers];
      state.availableClassroomMyBookings = payload?.myBookings;
      state.availableClassroomsCount = payload.roomAndSlotsCount;
      return state;
    });
    builder.addCase(getAvailableClassrooms.rejected, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isAvailableClassroomsFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getAvailableClassrooms.pending, (state, { payload }) => {
      state.isClassroomSliceFetching = true;
      state.isClassroomSliceSmallFetching = true;
      state.isAvailableClassroomsFetching = true;
      return state;
    });

    // Eduspace calendar feed. Lands in the same list as getAvailableClassrooms —
    // the calendar reads one place — but does not touch `towersList` or
    // `availableClassroomMyBookings`, which this endpoint deliberately omits and
    // other pages still depend on.
    builder.addCase(getEduspaceCalendar.pending, (state) => {
      state.isClassroomSliceFetching = true;
      state.isClassroomSliceSmallFetching = true;
      state.isAvailableClassroomsFetching = true;
      return state;
    });
    builder.addCase(getEduspaceCalendar.fulfilled, (state, { payload, meta }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isAvailableClassroomsFetching = false;
      // Drop a response requested under a module the user has since left, so a
      // slow "load more" can't append the old module's rooms into the new list.
      const requestModule = payload?.[REQUEST_MODULE_KEY];
      if (state.dataModule && requestModule && requestModule !== state.dataModule) {
        return state;
      }
      const incomingRooms = payload?.roomAndSlots || [];
      // Page 1 REPLACES (the list is shared across modules); "load more" appends.
      const isFirstPage = (meta?.arg?.pageNumber ?? 1) <= 1;
      state.availableClassroomsList = isFirstPage
        ? incomingRooms
        : [...state.availableClassroomsList, ...incomingRooms];
      state.availableClassroomsCount = payload.roomAndSlotsCount;
      return state;
    });
    builder.addCase(getEduspaceCalendar.rejected, (state, { payload }) => {
      state.isClassroomSliceFetching = false;
      state.isClassroomSliceSmallFetching = false;
      state.isAvailableClassroomsFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });

    // Get request comments
    builder.addCase(getRequestComments.fulfilled, (state, { payload }) => {
      state.isCommentsFetching = false;
      state.requestCommentsList = payload?.data?.comments || [];
      state.requestCommentsCount = payload?.data?.totalCount || 0;
      return state;
    });
    builder.addCase(getRequestComments.rejected, (state, { payload }) => {
      state.isCommentsFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(getRequestComments.pending, (state) => {
      state.isCommentsFetching = true;
      return state;
    });

    // Add request comment
    builder.addCase(addRequestComment.fulfilled, (state, { payload }) => {
      state.isPostingComment = false;
      state.commentPostSuccess = true;
      // Append the newly created comment so the list updates without a refetch.
      if (payload?.data) {
        state.requestCommentsList = [...state.requestCommentsList, payload.data];
        state.requestCommentsCount = state.requestCommentsCount + 1;
      }
      return state;
    });
    builder.addCase(addRequestComment.rejected, (state, { payload }) => {
      state.isPostingComment = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(addRequestComment.pending, (state) => {
      state.isPostingComment = true;
      state.commentPostSuccess = false;
      return state;
    });

    // Find slots for one classroom
    builder.addCase(findSlotsForOneClassroom.fulfilled, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.slotsForOneClassroom = payload?.roomAndSlots;
      state.availableClassroomMyBookings = payload?.myBookings;
      return state;
    });
    builder.addCase(findSlotsForOneClassroom.rejected, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = false;
      state.isClassroomSliceError = true;
      state.classroomSliceErrorMessage = payload?.message || "Something Went Wrong";
      return state;
    });
    builder.addCase(findSlotsForOneClassroom.pending, (state, { payload }) => {
      state.isClassroomSliceSmallFetching = true;
      return state;
    });

    // A request we aborted ourselves (a duplicate superseded by a newer one —
    // see useLatestRequest) is not a failure. Matchers run after the case
    // reducers, so this undoes the error state the `rejected` case just set and
    // keeps a cancelled duplicate from raising an error toast.
    builder.addMatcher(
      (action) => action.type?.endsWith("/rejected") && action.meta?.aborted,
      (state) => {
        state.isClassroomSliceError = false;
        state.classroomSliceErrorMessage = "";
      }
    );
  },
});

export const {
  clearClassroomSliceStates,
  clearClassroomSliceData,
  clearClassroomDetail,
  addClassroomDetails,
  addClassroomSelectedDate,
  addClassroomSelectedSlot,
  addClassroomSelectedSchool,
  clearClassroomSelectedSlot,
  addFinalMultipleSlotsForClassroomAdmin,
  clearFinalMultipleSlotsForClassroomAdmin,
  clearSelectedSingleClassRoom,
  clearRequestComments,
} = classroomSlice.actions;

export const classroomData = (state) => state.classroomData;
export default classroomSlice;
